import React from 'react';
import moment from 'moment';

const VoucherPrint = ({ data }) => {
  if (!data) return null;

  const {
    voucherNo,
    voucherDate,
    referenceNo,
    paymentMode,
    checkNo,
    checkDate,
    narration,
    description,
    amount,
    entries = [],
    party,
    partyName,
    type = 'Payment Voucher',
    company,
    companyAddress,
    companyPhone,
  } = data;

  const totalAmount = entries.length > 0
    ? entries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
    : parseFloat(amount) || 0;

  const amountInWords = (num) => {
    if (!num) return 'Zero Rupees Only';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
      'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convert = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };

    const intPart = Math.floor(num);
    const decPart = Math.round((num - intPart) * 100);
    let result = convert(intPart) + ' Rupees';
    if (decPart > 0) result += ' and ' + convert(decPart) + ' Paisa';
    return result + ' Only';
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      padding: '30px 40px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color: '#000',
      backgroundColor: '#fff',
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '12px' }}>
        {company && (
          <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '4px' }}>{company}</div>
        )}
        {companyAddress && (
          <div style={{ fontSize: '12px', color: '#444', marginBottom: '2px' }}>{companyAddress}</div>
        )}
        {companyPhone && (
          <div style={{ fontSize: '12px', color: '#444' }}>Ph: {companyPhone}</div>
        )}
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '10px', textTransform: 'uppercase', letterSpacing: '2px' }}>
          {type}
        </div>
      </div>

      {/* Voucher Info Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              {voucherNo && (
                <tr>
                  <td style={{ paddingRight: '12px', fontWeight: 'bold', paddingBottom: '6px' }}>Voucher No:</td>
                  <td style={{ paddingBottom: '6px' }}>{voucherNo}</td>
                </tr>
              )}
              {referenceNo && (
                <tr>
                  <td style={{ paddingRight: '12px', fontWeight: 'bold', paddingBottom: '6px' }}>Reference No:</td>
                  <td style={{ paddingBottom: '6px' }}>{referenceNo}</td>
                </tr>
              )}
              {(party || partyName) && (
                <tr>
                  <td style={{ paddingRight: '12px', fontWeight: 'bold', paddingBottom: '6px' }}>Party:</td>
                  <td style={{ paddingBottom: '6px' }}>{partyName || party}</td>
                </tr>
              )}
              {paymentMode && (
                <tr>
                  <td style={{ paddingRight: '12px', fontWeight: 'bold', paddingBottom: '6px' }}>Payment Mode:</td>
                  <td style={{ paddingBottom: '6px', textTransform: 'capitalize' }}>{paymentMode}</td>
                </tr>
              )}
              {checkNo && (
                <tr>
                  <td style={{ paddingRight: '12px', fontWeight: 'bold', paddingBottom: '6px' }}>Cheque No:</td>
                  <td style={{ paddingBottom: '6px' }}>{checkNo}</td>
                </tr>
              )}
              {checkDate && (
                <tr>
                  <td style={{ paddingRight: '12px', fontWeight: 'bold', paddingBottom: '6px' }}>Cheque Date:</td>
                  <td style={{ paddingBottom: '6px' }}>{moment(checkDate).format('DD-MMM-YYYY')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
            Date: {voucherDate ? moment(voucherDate).format('DD-MMM-YYYY') : moment().format('DD-MMM-YYYY')}
          </div>
        </div>
      </div>

      {/* Entries Table */}
      {entries.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>#</th>
              <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Account</th>
              <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Description</th>
              <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Debit</th>
              <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Credit</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #ccc', padding: '7px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #ccc', padding: '7px' }}>{entry.account || entry.accountName || '-'}</td>
                <td style={{ border: '1px solid #ccc', padding: '7px' }}>{entry.description || entry.narration || '-'}</td>
                <td style={{ border: '1px solid #ccc', padding: '7px', textAlign: 'right' }}>
                  {entry.debit ? parseFloat(entry.debit).toLocaleString() : '-'}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '7px', textAlign: 'right' }}>
                  {entry.credit ? parseFloat(entry.credit).toLocaleString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
              <td colSpan={3} style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Total</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>
                {entries.reduce((s, e) => s + (parseFloat(e.debit) || 0), 0).toLocaleString()}
              </td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>
                {entries.reduce((s, e) => s + (parseFloat(e.credit) || 0), 0).toLocaleString()}
              </td>
            </tr>
          </tfoot>
        </table>
      ) : (
        /* Simple single amount layout */
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Description</th>
              <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Amount (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{narration || description || '-'}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>
                {totalAmount ? totalAmount.toLocaleString() : '0'}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Total</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>
                Rs. {totalAmount ? totalAmount.toLocaleString() : '0'}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {/* Amount in Words */}
      <div style={{
        border: '1px solid #ccc',
        padding: '8px 12px',
        marginBottom: '20px',
        backgroundColor: '#fafafa',
      }}>
        <span style={{ fontWeight: 'bold' }}>Amount in Words: </span>
        <span>{amountInWords(totalAmount)}</span>
      </div>

      {/* Narration */}
      {(narration || description) && entries.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <span style={{ fontWeight: 'bold' }}>Narration: </span>
          <span>{narration || description}</span>
        </div>
      )}

      {/* Signatures */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '50px',
        paddingTop: '10px',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: '140px', marginBottom: '4px' }}></div>
          <div style={{ fontSize: '12px' }}>Prepared By</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: '140px', marginBottom: '4px' }}></div>
          <div style={{ fontSize: '12px' }}>Checked By</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: '140px', marginBottom: '4px' }}></div>
          <div style={{ fontSize: '12px' }}>Authorized By</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: '140px', marginBottom: '4px' }}></div>
          <div style={{ fontSize: '12px' }}>Receiver</div>
        </div>
      </div>
    </div>
  );
};

export default VoucherPrint;
